import { ReelEvent, SlingoEvent } from "@lib/titan2-slingo/dist/game/controller/event/SlingoEvent";
import { ReelController } from "@lib/titan2-slingo/dist/game/controller/ReelController";
import { AppEvent, GameErrorEvent } from "@lib/titan2/dist/game/Constants";
import { GameEvent } from "./event/GameEvent";
import { SymbolType } from "@lib/titan2-slingo/dist/api/model/enum/SymbolType";
import { TweenConstants } from "@lib/titan2-slingo/dist/game/view/abstract/constant/TweenConstants";
import { GameTweenConstants } from "../view/abstract/constant/GameTweenConstants";
import { Service } from "@lib/titan2-slingo/dist/api/Service";
/**
 * GameReelController.ts
 */
export class GameReelController extends ReelController {
    postCreate() {
        super.postCreate();
        this._waitForError = Promise.resolve();
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerGame(SlingoEvent.SCATTER_WIN_DISAPPEAR, this.handleScatterDisappear);
        this.registerGame(GameEvent.COIN_WIN_COMPLETE, this.handleCoinWinComplete);
        this.registerCore(GameErrorEvent.WAGER_BONUS, this.handleWagerError);
        this.registerCore(GameErrorEvent.WAGER_CASH, this.handleWagerError);
        this.registerCore(AppEvent.ERROR_COMPLETE, this.handleErrorComplete);
    }
    handleReelAwards() {
        this.dispatchGame(SlingoEvent.REELS_AWARD);
        this._scatterWinDone = !this.model.getSpinResult().getSymbolWins().filter(win => win.getType() === undefined).length; /// undefined winTypes are scatters
        this._coinWinDone = !this.model.getSpinResult().getSymbolWins().filter(win => win.getType() === SymbolType.COIN).length;
        this.determineReelsAwardBehaviour();
    }
    handleReelStopped() {
        const scatterThreshold = this.model.getScatterThreshold();
        let stopIndex = this.model.getReelDisplayData().getStopIndex();
        if (stopIndex !== undefined) {
            let symbol = this.reelDisplayData.getDisplaySymbols()[stopIndex];
            if (symbol.isJokerOrSuperJoker()) {
                this.model.setScatterCount(this.model.getScatterCount() + 1);
            }
            this.handleReelSymbol(stopIndex, symbol);
            this.model.getReelDisplayData().stopReel();
            if (this.model.getReelDisplayData().isStopComplete()) {
                this.handleReelAwards();
            }
            else if (scatterThreshold >= 0 && this.model.getScatterCount() >= scatterThreshold) {
                this.callAfter(GameTweenConstants.REEL_STOP_INTERVAL_SUSPENSE, this.stopReel);
            }
            else {
                const extraSpins = this.model.getGameProgress().getSpinsRemaining() === 0;
                const delay = extraSpins ? TweenConstants.REEL_STOP_INTERVAL_PURCHASE : TweenConstants.REEL_STOP_INTERVAL_STANDARD;
                this.callAfter(delay, this.stopReel);
            }
        }
    }
    handleReelSymbol(index, symbol) {
        const darkenedReels = this.model.getReelDisplayData().getDarkenedReels();
        this.dispatchGame(SlingoEvent.REEL_STRIKE);
        if (symbol.isJoker()) {
            if (darkenedReels.indexOf(index) >= 0) {
                this.reelDisplayData.setDisabledIndexes([index]);
                this.dispatchGame(ReelEvent.DISABLE_REELS);
            }
            else {
                this.dispatchGame(SlingoEvent.REEL_STRIKE_JOKER);
            }
        }
        else if (symbol.isSuperJoker()) {
            this.dispatchGame(SlingoEvent.REEL_STRIKE_SUPER_JOKER);
        }
        else if (symbol.isFreeSpin()) {
            this.dispatchGame(SlingoEvent.REEL_STRIKE_FREE_SPIN);
        }
        else if (symbol.isDevil()) {
            this.dispatchGame(SlingoEvent.REEL_STRIKE_DEVIL);
        }
        else if (symbol.isCoin()) {
            this.dispatchGame(SlingoEvent.REEL_STRIKE_COIN);
        }
        else {
            this.dispatchGame(SlingoEvent.REEL_STRIKE_NUMBER);
        }
    }
    determineReelsAwardBehaviour() {
        if (!this._scatterWinDone) {
            this.handleScatterWin();
        }
        else if (!this._coinWinDone) {
            this.handleCoinWin();
        }
        else {
            this.dispatchGame(SlingoEvent.REELS_COMPLETE);
        }
    }
    handleScatterWin() {
        this.dispatchGame(SlingoEvent.SCATTER_WIN_ANIMATION);
    }
    handleCoinWin() {
        this.dispatchGame(GameEvent.COIN_WIN_START);
    }
    handleCoinWinComplete() {
        this._coinWinDone = true;
        this.determineReelsAwardBehaviour();
    }
    handleScatterDisappear() {
        this._scatterWinDone = true;
        this.determineReelsAwardBehaviour();
    }
    handleWagerError() {
        this._waitForError = new Promise(resolve => this._waitForErrorResolve = resolve);
    }
    handleErrorComplete() {
        if (this._waitForErrorResolve) {
            this._waitForErrorResolve();
            this._waitForError = Promise.resolve();
            this._waitForErrorResolve = null;
        }
    }
    handleSpinResult() {
        this._waitForError.then(() => {
            super.handleSpinResult();
        });
    }
    handleDabSuccess() {
        if (!Service.isReplay()) {
            super.handleDabSuccess();
            return;
        }
        // jokers and super-jokers are not represented in the normal way during replay. We don't get told which autodabbed cells were super-jokers, so we need to work out where the superjoker is on the reels
        const dabData = this.model.getDabData();
        const selectedCell = dabData.getSelectedCell();
        const reelSequence = this.model.getSpinResult().getReelSequence();
        const superJokerCells = [];
        let lastSeenSuperJokerReelIndex = -1;
        let lastSeenJokerReelIndex = -1;
        dabData.getAutoDabCells().forEach(cell => {
            for (const [reelIndex, symbol] of reelSequence.getSymbols().entries()) {
                if (symbol.getId() === cell.getNumber().toString()) { // it's an ordinary number match in this reel
                    break;
                }
                if (symbol.isJoker()
                    && reelSequence.getJokerIndexes().includes(reelIndex)
                    && cell.getColumn() === reelIndex
                    && reelIndex > lastSeenJokerReelIndex) { // it's a joker in this reel
                    lastSeenJokerReelIndex = reelIndex;
                    break;
                }
                if (symbol.isSuperJoker() && reelIndex > lastSeenSuperJokerReelIndex) { // if it's not a number match, or a joker, it must be a superjoker, somewhere on the reel. consume them left to right
                    superJokerCells.push(cell.getNumber());
                    lastSeenSuperJokerReelIndex = reelIndex;
                    break;
                }
            }
        });
        const superJokerIndex = superJokerCells.indexOf(selectedCell.getNumber());
        const isSuperJokerCell = superJokerIndex >= 0;
        const superJokerReels = reelSequence.getSuperJokerIndexes();
        const superJokerColumn = superJokerReels[superJokerIndex];
        const column = isSuperJokerCell ? superJokerColumn : selectedCell.getColumn();
        this.reelDisplayData.setDisabledIndexes([column]);
        if (column <= this.reelDisplayData.getNumberOfReels()) {
            this.dispatchGame(ReelEvent.DISABLE_REELS);
        }
    }
}
//# sourceMappingURL=GameReelController.js.map